import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { vaccinationApi, type VaxObservation } from "../../shared/api/vaccination";
import { apiError } from "../../shared/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const REACTION_OPTIONS: { value: string; label: string }[] = [
  { value: "NONE", label: "Không phản ứng" },
  { value: "LOCAL", label: "Phản ứng tại chỗ" },
  { value: "SYSTEMIC", label: "Phản ứng toàn thân" },
  { value: "SEVERE", label: "Nặng" },
];

/**
 * Tiêm chủng · Bảng theo dõi sau tiêm (P0B). Live board of patients under the 30-minute
 * post-injection watch, with elapsed/remaining time and a one-click "đủ điều kiện ra về".
 */
export function VaccinationObservationBoardPage() {
  const qc = useQueryClient();
  const board = useQuery({
    queryKey: ["vaccination", "observation-board"],
    queryFn: () => vaccinationApi.observationBoard(),
    refetchInterval: 15000,
  });
  const complete = useMutation({
    mutationFn: ({ id, reaction, assessed }: { id: string; reaction: string; assessed: boolean }) =>
      vaccinationApi.completeObservation(id, { reaction, conclusion: "CLEARED", assessed }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["vaccination", "observation-board"] }),
    onError: (e) => setErr(apiError(e).detail),
  });
  const [err, setErr] = useState<string | null>(null);

  return (
    <div>
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-foreground">Bảng theo dõi sau tiêm</h1>
          <p className="text-sm text-muted-foreground">
            Bệnh nhân đang theo dõi 30 phút sau tiêm. Bảng tự làm mới mỗi 15 giây.
          </p>
        </div>
      </div>

      {err && (
        <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {err}
        </div>
      )}

      {(board.data?.length ?? 0) === 0 && (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            Không có bệnh nhân nào đang theo dõi.
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {board.data?.map((o) => (
          <ObservationCard
            key={o.id}
            o={o}
            onComplete={(reaction, assessed) => {
              setErr(null);
              complete.mutate({ id: o.id, reaction, assessed });
            }}
          />
        ))}
      </div>
    </div>
  );
}


/**
 * Why this patient may not go home yet, or null when they may (#18809).
 *
 * <p>Extracted from the card's JSX so the four clinical gates can be tested directly rather than
 * through a dropdown. They are the whole point of the board: a refactor that loosened one would
 * not look like a bug, it would look like a faster workflow.
 *
 * <p>The backend re-validates the reaction gate and is the final authority; this is the screen
 * refusing to ask for something that would be rejected.
 *
 * @param ready whether the monitoring period has elapsed
 * @param remaining minutes still to wait, used only for the message
 * @param assessed whether a clinician has ticked off a local reaction
 */
export function dischargeBlockReason(
  reaction: string,
  ready: boolean,
  remaining: number,
  assessed: boolean,
): string | null {
  // #18665 — no default reaction: staff must consciously pick a status before discharge.
  if (!reaction) return "Chọn tình trạng phản ứng để tiếp tục.";
  if (!ready) return `Chưa đủ thời gian theo dõi — còn ${remaining}′.`;
  if (reaction === "SYSTEMIC" || reaction === "SEVERE")
    return "Phản ứng toàn thân/nặng — chưa đủ điều kiện ra về.";
  if (reaction === "LOCAL" && !assessed)
    return "Phản ứng tại chỗ — cần NVYT đánh giá trước khi cho ra về.";
  return null;
}

function ObservationCard({
  o,
  onComplete,
}: {
  o: VaxObservation;
  onComplete: (reaction: string, assessed: boolean) => void;
}) {
  // #18665 — no default reaction: staff must consciously pick a status before discharge.
  const [reaction, setReaction] = useState("");
  const [assessed, setAssessed] = useState(false);
  const started = new Date(o.startedAt).getTime();
  const elapsedMin = Math.max(0, Math.floor((Date.now() - started) / 60000));
  const remaining = Math.max(0, o.monitorMinutes - elapsedMin);
  const ready = remaining === 0;

  const reason = dischargeBlockReason(reaction, ready, remaining, assessed);
  const canDischarge = reason === null;

  return (
    <Card className={`border-l-4 ${ready ? "border-l-emerald-500" : "border-l-primary"}`}>
      <CardContent className="p-4">
        <div className="font-bold text-foreground">
          {o.patientName ? o.patientName.toUpperCase() : `BN ${o.patientId.slice(0, 8)}…`}
          {o.patientBirthYear ? ` · ${o.patientBirthYear}` : ""}
        </div>
        {o.patientCode && (
          <div className="font-mono text-xs text-muted-foreground">{o.patientCode}</div>
        )}
        <div className="mt-0.5 text-sm text-muted-foreground">
          Đã theo dõi {elapsedMin}′ · {ready ? "Đủ 30′ — sẵn sàng đánh giá" : `còn ${remaining}′`}
        </div>
        <div className="mt-3">
          <Select
            value={reaction}
            onValueChange={(v) => {
              setReaction(v);
              setAssessed(false);
            }}
          >
            <SelectTrigger className="w-full" aria-label="Phản ứng sau tiêm">
              <SelectValue placeholder="— Chọn tình trạng phản ứng —" />
            </SelectTrigger>
            <SelectContent>
              {REACTION_OPTIONS.map((r) => (
                <SelectItem key={r.value} value={r.value}>
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {reaction === "LOCAL" && (
          <label className="mt-2.5 flex cursor-pointer items-start gap-2 text-sm">
            <Checkbox checked={assessed} onCheckedChange={(v) => setAssessed(v === true)} />
            <span>Đã đánh giá và đủ điều kiện ra về (NVYT xác nhận).</span>
          </label>
        )}

        <Button
          type="button"
          className="mt-3 w-full"
          disabled={!canDischarge}
          onClick={() => onComplete(reaction, assessed)}
        >
          Đủ ĐK ra về
        </Button>
        {reason && <div className="mt-1.5 text-xs text-muted-foreground">{reason}</div>}
      </CardContent>
    </Card>
  );
}
